import type { User } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';

export type DashboardAccessInitialization = {
    therapistId: string;
    providerId: string;
    role: string;
    features: string[];
};

const initializationsByScope = new Map<string, Promise<DashboardAccessInitialization>>();

/** Bound remote auth work so a failed SDK request never leaves the login UI spinning forever. */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error(message)), timeoutMs);
        promise.then(
            (value) => {
                window.clearTimeout(timeout);
                resolve(value);
            },
            (error) => {
                window.clearTimeout(timeout);
                reject(error);
            }
        );
    });
}

// Login and the auth observer can fire together, so deduplicate by user and requested provider scope.
export function initializeDashboardAccess(
    user: User,
    email: string | null,
    requestedProviderId = ''
): Promise<DashboardAccessInitialization> {
    const initializationScope = `${user.uid}:${requestedProviderId}`;
    const existingInitialization = initializationsByScope.get(initializationScope);
    if (existingInitialization) {
        return existingInitialization;
    }

    const initialization = (async () => {
        const setUserClaims = httpsCallable<
            { email: string | null; providerId?: string },
            {
                therapistId?: string | null;
                providerId?: string | null;
                role?: string | null;
                features?: unknown;
            }
        >(getFunctions(), 'setUserClaims');
        const result = await withTimeout(
            setUserClaims({
                email,
                ...(requestedProviderId ? {providerId: requestedProviderId} : {}),
            }),
            20_000,
            'Dashboard access setup timed out. Check your connection and try signing in again.'
        );
        const therapistId = result.data.therapistId || '';
        const providerId = result.data.providerId || '';
        const role = result.data.role || '';
        const features = result.data.features;
        if (!therapistId || !providerId || !role || !Array.isArray(features) ||
            !features.every((feature) => typeof feature === 'string')) {
            throw new Error('Access denied. This account is not authorized to access this application.');
        }

        // Firestore uses the server-owned DashboardAccessGrant written by the
        // callable. Refresh legacy Storage claims in the background so a slow
        // Secure Token response cannot block dashboard entry.
        void user.getIdTokenResult(true).catch((error) =>
            console.warn('DASHBOARD_AUTH: Background token refresh did not complete:', error)
        );

        return {
            therapistId,
            providerId,
            role,
            features,
        };
    })();

    initializationsByScope.set(initializationScope, initialization);
    initialization.then(
        () => {
            if (initializationsByScope.get(initializationScope) === initialization) {
                initializationsByScope.delete(initializationScope);
            }
        },
        () => {
            if (initializationsByScope.get(initializationScope) === initialization) {
                initializationsByScope.delete(initializationScope);
            }
        }
    );
    return initialization;
}
